import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useParams } from "react-router-dom";

interface IssueDetailsProps {
  issue: {
    id: string;
    category: string;
    dateReported: string;
    status: "NEW" | "IN_REVIEW" | "RESOLVED";
    lat: number;
    lng: number;
  };
}

const IssueDetails = ({ issue }: any) => {
  const [status, setStatus] = useState<string>(issue.status);
  const { id: reportId } = useParams<{ id: string }>();

  const handleStatusChange = (value: string) => {
    setStatus(value);
  };

  const saveStatus = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_BE_URL}/api/v1/reports/${reportId}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: status,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update status");
      }

      const data: any = await response.json();

      toast.success(
        `Status updated to ${status
          .replace(/-/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase())}`
      );

      return data.report; // Return updated report data if needed
    } catch (error) {
      console.error("Update error:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to update status"
      );
      throw error; // Re-throw if you need to handle it in the calling component
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "NEW":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "IN_REVIEW":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "RESOLVED":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  // Format category display by replacing underscores with spaces
  const formatCategoryDisplay = (category: string) => {
    return category.replace(/_/g, ' ');
  };

  return (
    <Card className="border-l-2 border-t-2 border-primary/30">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg">Issue Details</CardTitle>
          <Badge className={`${getStatusColor(status)} border-0`}>
            {status === "in-progress"
              ? "In Progress"
              : status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Category</p>
              <p className="font-medium capitalize">{formatCategoryDisplay(issue.category)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Issue ID</p>
              <p className="font-medium">{issue.reportId}</p>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Clock className="h-4 w-4" />
              <span>Reported On</span>
            </div>
            <p className="font-medium">{issue.dateReported}</p>
          </div>

          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <MapPin className="h-4 w-4" />
              <span>Location</span>
            </div>
            <p className="font-medium font-mono">
              {issue.lat}, {issue.lng}
            </p>
          </div>

          <div className="pt-4 border-t border-border">
            <label className="text-sm font-medium mb-2 block">
              Update Status
            </label>
            <div className="flex gap-2">
              <Select value={status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEW">New</SelectItem>
                  <SelectItem value="IN_REVIEW">In Review</SelectItem>
                  <SelectItem value="RESOLVED">Resolved</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={saveStatus}>Save</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default IssueDetails;
